/**
 * A deliberately small SVG reader.
 *
 * This is an ALLOWLIST PARSER, not a sanitizer, and the distinction is the whole
 * security argument. The markup is never handed to a DOM: it is scanned as text,
 * every element and attribute not on the list is a hard rejection, and what
 * comes out the other side is Maipo's own geometry. There is no path by which a
 * script, an external reference, or an entity expansion reaches anything —
 * because nothing is ever executed, resolved, or inserted.
 *
 * That is a much smaller claim than "we sanitize SVG", and it is the reason
 * import can ship at all: the repo's own security notes block SVG pending
 * sanitization, and sanitizing arbitrary SVG safely is a project. Reading a
 * defined subset and refusing the rest is not.
 *
 * Refusals are loud. A file this cannot represent produces an error naming what
 * it found, never a document that silently lost half its artwork.
 */

import { IDENTITY_MATRIX, multiplyMatrix, type Matrix } from '../../core/geometry';
import type { PathCommand } from '../../vector/types';

export const SVG_LIMITS = {
  maxSourceBytes: 8 * 1024 * 1024,
  maxElements: 20_000,
  maxDepth: 64,
  maxTotalCommands: 400_000,
  maxTokenChars: 40,
} as const;

export class SvgParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SvgParseError';
  }
}

/** Quotes file-derived text safely: truncated, one line, no control characters. */
function describeToken(raw: string): string {
  const cleaned = [...raw.slice(0, SVG_LIMITS.maxTokenChars)]
    .map((character) => (character.charCodeAt(0) < 0x20 || character.charCodeAt(0) === 0x7f ? ' ' : character))
    .join('')
    .trim();
  return `“${cleaned}${raw.length > SVG_LIMITS.maxTokenChars ? '…' : ''}”`;
}

/* ---------- scanning ---------- */

export interface SvgElement {
  readonly name: string;
  readonly attributes: ReadonlyMap<string, string>;
  readonly depth: number;
  readonly selfClosing: boolean;
}

const ALLOWED_ELEMENTS = new Set([
  'svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'line',
  // Definitions. Read for what they define, never drawn.
  'defs', 'clippath', 'lineargradient', 'radialgradient', 'stop',
]);
/** Read but ignored: human-facing text that carries no geometry. */
const IGNORED_ELEMENTS = new Set(['title', 'desc', 'metadata']);
/**
 * Editor-private namespaces. Elements in them (Inkscape's namedview, RDF
 * licence blocks, Illustrator's private data) are skipped with their contents
 * and their attributes dropped: no renderer draws them. Matching is by the
 * namespace URI the file binds, not by prefix, so a prefix pointed anywhere
 * else is still refused rather than silently skipped.
 */
const EDITOR_NAMESPACES = new Set([
  'http://www.inkscape.org/namespaces/inkscape',
  'http://sodipodi.sourceforge.net/DTD/sodipodi-0.dtd',
  'http://www.bohemiancoding.com/sketch/ns',
  'http://www.serif.com/',
  'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  'http://purl.org/dc/elements/1.1/',
  'http://creativecommons.org/ns#',
  'http://web.resource.org/cc/',
  'http://ns.adobe.com/AdobeIllustrator/10.0/',
  'http://ns.adobe.com/Extensibility/1.0/',
  'adobe:ns:meta/',
]);
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';

function decodeAttribute(value: string): string {
  // Entities are rejected outright by the scanner, so a value is already literal.
  return value;
}

/** Resolve a deliberately bounded inline CSS subset before reading geometry.
 * Style declarations override presentation attributes. Unsupported CSS is an
 * explicit refusal, never a browser-dependent or silently discarded value.
 */
export function inlinePaintAttributes(source: ReadonlyMap<string, string>): ReadonlyMap<string, string> {
  const style = source.get('style');
  if (style === undefined || style.trim() === '') return source;
  if (/[\\{}"']|\/\*|\*\//.test(style)) {
    throw new SvgParseError('This SVG uses inline style syntax this reader does not support. Expand styles to presentation attributes.');
  }
  const supported = new Set(['fill', 'stroke', 'fill-rule', 'fill-opacity', 'stop-color', 'display', 'visibility', 'opacity']);
  const declarations = new Map<string, { value: string; important: boolean }>();
  for (const raw of style.split(';')) {
    if (raw.trim() === '') continue;
    const declaration = /^\s*([a-z-]+)\s*:\s*(.*?)\s*$/i.exec(raw);
    if (!declaration || !declaration[2]) throw new SvgParseError('This SVG has a malformed inline style declaration.');
    const property = declaration[1]!.toLowerCase();
    if (!supported.has(property)) throw new SvgParseError(`This SVG uses unsupported inline style property ${describeToken(property)}. Expand or flatten this style before importing.`);
    const important = /!\s*important\s*$/i.test(declaration[2]);
    const value = declaration[2].replace(/!\s*important\s*$/i, '').trim();
    if (!value || value.includes('!') || /(?:var|env)\s*\(/i.test(value)) throw new SvgParseError('This SVG has an unsupported inline style value.');
    const previous = declarations.get(property);
    if (!previous?.important || important) declarations.set(property, { value, important });
  }
  const attributes = new Map(source);
  for (const [property, declaration] of declarations) attributes.set(property, declaration.value);
  return attributes;
}

/**
 * Walks the markup as text, emitting allowed elements.
 *
 * Rejects, rather than tolerates: entities of any kind, CDATA, a doctype with
 * an internal subset or after the root, processing instructions beyond one
 * leading XML declaration, namespaced names outside the editor-private
 * namespaces, duplicate attributes, and anything past the size, depth or
 * element budgets. Every one of those is a vector in a sanitizer and simply not
 * representable here.
 *
 * Skipped as inert: comments, one external-identifier-only doctype before the
 * root (nothing is ever fetched), editor-private elements and attributes,
 * `xml:space`/`xml:lang` (inert without `<text>`, which is refused), and
 * `xlink:href` read as `href` for local `#id` references only.
 */
export function scanSvg(text: string, onElement: (element: SvgElement) => void, onClose: (name: string) => void): void {
  let at = 0;
  let depth = 0;
  let elements = 0;
  let doctype = false;
  const open: string[] = [];
  // Namespace bindings are scoped to their element and its descendants, one
  // frame per open element, so a prefix bound inside one subtree never changes
  // what a later sibling means.
  const frames: ReadonlyMap<string, string>[] = [new Map()];
  const noEntities = (segment: string) => {
    if (segment.includes('&')) throw new SvgParseError('This SVG uses XML entities, which this reader does not resolve. Save it with entities expanded.');
  };

  if (text.charCodeAt(0) === 0xfeff) at = 1;
  // A single leading XML declaration is conventional and harmless.
  const declaration = text.indexOf('<?xml', at);
  if (declaration === at && at < text.length) {
    const end = text.indexOf('?>', at);
    if (end < 0) throw new SvgParseError('The XML declaration is not terminated.');
    at = end + 2;
  }

  while (at < text.length) {
    const next = text.indexOf('<', at);
    // Character data between tags is the one place, besides attribute values,
    // where an entity reference could be read; both are checked in this pass.
    noEntities(next < 0 ? text.slice(at) : text.slice(at, next));
    if (next < 0) break;
    at = next + 1;
    if (text.startsWith('!--', at)) {
      const close = text.indexOf('-->', at + 3);
      if (close < 0) throw new SvgParseError('A comment is not terminated.');
      at = close + 3;
      continue;
    }
    if (/^!doctype\s/i.test(text.slice(at, at + 9))) {
      // An external identifier is never fetched; an internal subset is where
      // entity declarations live, so it stays refused. Quoted identifiers may
      // hold '>' or '[', so the end is found outside quotes.
      if (doctype || elements > 0) throw new SvgParseError('This SVG declares more than one doctype, or one after the root element, which this reader rejects.');
      doctype = true;
      let quote = '';
      let close = at + 8;
      for (; close < text.length; close += 1) {
        const character = text[close]!;
        if (quote) { if (character === quote) quote = ''; continue; }
        if (character === '"' || character === "'") quote = character;
        else if (character === '[') throw new SvgParseError('This SVG declares a doctype with an internal subset, which this reader rejects.');
        else if (character === '>') break;
      }
      if (close >= text.length) throw new SvgParseError('The doctype is not terminated.');
      at = close + 1;
      continue;
    }
    if (text[at] === '!') throw new SvgParseError('This SVG contains a CDATA section, which this reader rejects.');
    if (text[at] === '?') throw new SvgParseError('This SVG contains a processing instruction, which this reader rejects.');

    if (text[at] === '/') {
      const end = text.indexOf('>', at);
      if (end < 0) throw new SvgParseError('An end tag is not terminated.');
      const name = text.slice(at + 1, end).trim().toLowerCase();
      const expected = open.pop();
      if (expected !== name) throw new SvgParseError(`Tags are not balanced: found ${describeToken(name)} where ${describeToken(expected ?? 'nothing')} should close.`);
      frames.pop();
      depth -= 1;
      onClose(name);
      at = end + 1;
      continue;
    }

    const end = text.indexOf('>', at);
    if (end < 0) throw new SvgParseError('A start tag is not terminated.');
    let body = text.slice(at, end);
    const selfClosing = body.endsWith('/');
    if (selfClosing) body = body.slice(0, -1);

    const nameEnd = body.search(/[\s/]/);
    const rawName = (nameEnd < 0 ? body : body.slice(0, nameEnd)).trim();
    const name = rawName.toLowerCase();

    elements += 1;
    if (elements > SVG_LIMITS.maxElements) throw new SvgParseError(`This SVG has more than ${SVG_LIMITS.maxElements.toLocaleString()} elements.`);

    const raw: [string, string][] = [];
    const attributePattern = /([A-Za-z_][-\w.:]*)\s*=\s*("([^"]*)"|'([^']*)')/g;
    const rest = nameEnd < 0 ? '' : body.slice(nameEnd);
    let match: RegExpExecArray | null;
    let consumed = 0;
    while ((match = attributePattern.exec(rest)) !== null) {
      if (rest.slice(consumed, match.index).trim() !== '') throw new SvgParseError('This SVG has malformed attributes; attribute values must be quoted.');
      consumed = attributePattern.lastIndex;
      const value = match[3] ?? match[4] ?? '';
      noEntities(value);
      raw.push([match[1]!, decodeAttribute(value)]);
    }
    // Namespace declarations first, so a prefix declared after its use on the
    // same element still resolves. They carry no geometry.
    let scope = frames[frames.length - 1]!;
    for (const [key, value] of raw) {
      if (!key.toLowerCase().startsWith('xmlns:')) continue;
      if (scope === frames[frames.length - 1]) scope = new Map(scope);
      (scope as Map<string, string>).set(key.slice(6), value.trim());
    }
    const editorPrefix = (prefix: string) => EDITOR_NAMESPACES.has(scope.get(prefix) ?? '');
    const attributes = new Map<string, string>();
    for (const [rawKey, value] of raw) {
      const key = rawKey.toLowerCase();
      if (key === 'xmlns' || key.startsWith('xmlns:')) continue;
      const colon = rawKey.indexOf(':');
      if (colon >= 0) {
        const prefix = rawKey.slice(0, colon);
        const local = key.slice(colon + 1);
        if (prefix === 'xml' && (local === 'space' || local === 'lang')) continue;
        if (editorPrefix(prefix)) continue;
        if (scope.get(prefix) === XLINK_NAMESPACE && local === 'href' && value.trim().startsWith('#')) {
          if (attributes.has('href')) throw new SvgParseError('The attribute “href” appears twice on one element.');
          attributes.set('href', value);
          continue;
        }
        throw new SvgParseError(`This SVG uses the namespaced attribute ${describeToken(key)}, which this reader rejects.`);
      }
      if (attributes.has(key)) throw new SvgParseError(`The attribute ${describeToken(key)} appears twice on one element.`);
      attributes.set(key, value);
    }

    if (rest.slice(consumed).trim() !== '') throw new SvgParseError('This SVG has malformed attributes; attribute values must be quoted.');

    if (!selfClosing && depth + 1 > SVG_LIMITS.maxDepth) throw new SvgParseError('This SVG nests deeper than this reader allows.');

    const colon = rawName.indexOf(':');
    if (colon >= 0 && !editorPrefix(rawName.slice(0, colon))) {
      throw new SvgParseError(`This SVG uses the namespaced element ${describeToken(rawName)}, which this reader rejects.`);
    }
    if (colon >= 0 || IGNORED_ELEMENTS.has(name)) {
      // Consumed for well-formedness and budget, contents discarded.
      at = selfClosing ? end + 1 : skipElement(text, end + 1, rawName);
      continue;
    }

    if (!ALLOWED_ELEMENTS.has(name)) {
      throw new SvgParseError(`This SVG uses <${name}>, which this reader does not support. Only <svg>, <g> and <path> are read.`);
    }
    if (!selfClosing) { open.push(name); frames.push(scope); depth += 1; }
    onElement({ name, attributes, depth, selfClosing });
    if (selfClosing) onClose(name);
    at = end + 1;
  }
  if (open.length > 0) throw new SvgParseError('The SVG ends with unclosed tags.');
}

/**
 * Returns the offset just past the end tag that closes an element whose
 * contents are discarded. Matches whole names only and counts same-name
 * nesting, so a longer name or a nested twin cannot end the skip early.
 */
function skipElement(text: string, from: number, rawName: string): number {
  // The name becomes part of a pattern: only XML name characters, so '.' and
  // '-' are the only ones that need escaping.
  if (!/^[A-Za-z_][\w.:-]*$/.test(rawName)) throw new SvgParseError(`The element name ${describeToken(rawName)} is malformed.`);
  const tag = new RegExp(`<(/?)${rawName.replace(/[.-]/g, '\\$&')}(?=[\\s/>])`, 'g');
  tag.lastIndex = from;
  let nesting = 1;
  let match: RegExpExecArray | null;
  while ((match = tag.exec(text)) !== null) {
    const close = text.indexOf('>', match.index);
    if (close < 0) break;
    if (match[1]) {
      nesting -= 1;
      if (nesting === 0) return close + 1;
    } else if (text[close - 1] !== '/') {
      nesting += 1;
    }
    tag.lastIndex = close + 1;
  }
  throw new SvgParseError(`A ${describeToken(rawName)} element is not closed.`);
}

/* ---------- numbers ---------- */

class NumberScanner {
  private at = 0;
  constructor(private readonly text: string) {}

  atEnd(): boolean {
    this.skip();
    return this.at >= this.text.length;
  }

  private skip(): void {
    while (this.at < this.text.length) {
      const code = this.text.charCodeAt(this.at);
      if (code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d || code === 0x2c) this.at += 1;
      else break;
    }
  }

  peekCommand(): string | null {
    this.skip();
    const character = this.text[this.at];
    return character !== undefined && /[A-Za-z]/.test(character) ? character : null;
  }

  takeCommand(): string {
    const character = this.peekCommand();
    if (character === null) throw new SvgParseError('Expected a path command.');
    this.at += 1;
    return character;
  }

  /** Own scanner rather than Number(): "1.2.3" and "1e" must be errors, not NaN. */
  number(): number {
    this.skip();
    const start = this.at;
    if (this.text[this.at] === '+' || this.text[this.at] === '-') this.at += 1;
    let digits = 0;
    while (/\d/.test(this.text[this.at] ?? '')) { this.at += 1; digits += 1; }
    if (this.text[this.at] === '.') {
      this.at += 1;
      while (/\d/.test(this.text[this.at] ?? '')) { this.at += 1; digits += 1; }
    }
    if (digits === 0) throw new SvgParseError(`Expected a number in the path data at ${describeToken(this.text.slice(start, start + 12))}.`);
    if (this.text[this.at] === 'e' || this.text[this.at] === 'E') {
      const mark = this.at;
      this.at += 1;
      if (this.text[this.at] === '+' || this.text[this.at] === '-') this.at += 1;
      let exponent = 0;
      while (/\d/.test(this.text[this.at] ?? '')) { this.at += 1; exponent += 1; }
      if (exponent === 0) this.at = mark;
    }
    const value = Number(this.text.slice(start, this.at));
    if (!Number.isFinite(value)) throw new SvgParseError('The path data contains a value that is not a finite number.');
    if (Math.abs(value) > 1e6) throw new SvgParseError('The path data contains a coordinate far outside any usable document.');
    return value;
  }

  flag(): boolean {
    this.skip();
    const character = this.text[this.at];
    if (character !== '0' && character !== '1') throw new SvgParseError('An elliptical arc flag must be 0 or 1.');
    this.at += 1;
    return character === '1';
  }
}

/* ---------- path data ---------- */

/** Splits an elliptical arc into cubics, which is the only form Maipo stores. */
function arcToCubics(x0: number, y0: number, rx: number, ry: number, rotation: number, largeArc: boolean, sweep: boolean, x: number, y: number): PathCommand[] {
  if (x0 === x && y0 === y) return [];
  if (rx === 0 || ry === 0) return [{ type: 'line', x, y }];
  let radiusX = Math.abs(rx);
  let radiusY = Math.abs(ry);
  const angle = (rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = (x0 - x) / 2;
  const dy = (y0 - y) / 2;
  const x1 = cos * dx + sin * dy;
  const y1 = -sin * dx + cos * dy;
  // Radii too small to span the endpoints are scaled up, per the SVG spec.
  const oversize = (x1 * x1) / (radiusX * radiusX) + (y1 * y1) / (radiusY * radiusY);
  if (oversize > 1) {
    const scale = Math.sqrt(oversize);
    radiusX *= scale;
    radiusY *= scale;
  }
  const denominator = radiusX * radiusX * y1 * y1 + radiusY * radiusY * x1 * x1;
  const numerator = Math.max(0, radiusX * radiusX * radiusY * radiusY - denominator);
  const factor = (largeArc === sweep ? -1 : 1) * Math.sqrt(denominator === 0 ? 0 : numerator / denominator);
  const cx1 = (factor * radiusX * y1) / radiusY;
  const cy1 = (-factor * radiusY * x1) / radiusX;
  const cx = cos * cx1 - sin * cy1 + (x0 + x) / 2;
  const cy = sin * cx1 + cos * cy1 + (y0 + y) / 2;
  const startAngle = Math.atan2((y1 - cy1) / radiusY, (x1 - cx1) / radiusX);
  let sweepAngle = Math.atan2((-y1 - cy1) / radiusY, (-x1 - cx1) / radiusX) - startAngle;
  if (!sweep && sweepAngle > 0) sweepAngle -= 2 * Math.PI;
  if (sweep && sweepAngle < 0) sweepAngle += 2 * Math.PI;

  const segments = Math.max(1, Math.ceil(Math.abs(sweepAngle) / (Math.PI / 2)));
  const step = sweepAngle / segments;
  const control = (4 / 3) * Math.tan(step / 4);
  const commands: PathCommand[] = [];
  let theta = startAngle;
  for (let index = 0; index < segments; index += 1) {
    const next = theta + step;
    const point = (t: number) => ({
      x: cos * radiusX * Math.cos(t) - sin * radiusY * Math.sin(t) + cx,
      y: sin * radiusX * Math.cos(t) + cos * radiusY * Math.sin(t) + cy,
    });
    const derivative = (t: number) => ({
      x: -cos * radiusX * Math.sin(t) - sin * radiusY * Math.cos(t),
      y: -sin * radiusX * Math.sin(t) + cos * radiusY * Math.cos(t),
    });
    const from = point(theta);
    const to = point(next);
    const dFrom = derivative(theta);
    const dTo = derivative(next);
    commands.push({
      type: 'cubic',
      x1: from.x + control * dFrom.x,
      y1: from.y + control * dFrom.y,
      x2: to.x - control * dTo.x,
      y2: to.y - control * dTo.y,
      x: to.x,
      y: to.y,
    });
    theta = next;
  }
  return commands;
}

/**
 * Full path grammar, normalised to the four commands Maipo stores.
 *
 * Relative forms, the shorthands `H V S T`, implicit command repetition, and
 * elliptical arcs all collapse here — the in-memory model has no arc and no
 * relative anything, so the alternative would be a lossy import that looked
 * fine until someone edited a node.
 */
export function parsePathData(d: string): PathCommand[] {
  const scanner = new NumberScanner(d);
  const commands: PathCommand[] = [];
  let x = 0;
  let y = 0;
  let startX = 0;
  let startY = 0;
  let previous = '';
  let lastControl: { x: number; y: number } | null = null;

  while (!scanner.atEnd()) {
    let command = scanner.peekCommand();
    if (command === null) {
      // Implicit repetition: a bare coordinate run continues the last command,
      // except that a repeated moveto means lineto.
      if (previous === '') throw new SvgParseError('The path data does not begin with a command.');
      if (previous === 'Z' || previous === 'z') throw new SvgParseError('The path data has coordinates after close-path without a new command.');
      command = previous === 'M' ? 'L' : previous === 'm' ? 'l' : previous;
    } else {
      scanner.takeCommand();
    }
    const relative = command === command.toLowerCase();
    const upper = command.toUpperCase();

    if (upper === 'Z') {
      commands.push({ type: 'close' });
      if (commands.length > SVG_LIMITS.maxTotalCommands) throw new SvgParseError('This SVG has more path geometry than this reader allows.');
      x = startX;
      y = startY;
      lastControl = null;
      previous = command;
      continue;
    }
    if (upper === 'M') {
      const nx = scanner.number() + (relative ? x : 0);
      const ny = scanner.number() + (relative ? y : 0);
      x = nx; y = ny; startX = nx; startY = ny;
      commands.push({ type: 'move', x, y });
      lastControl = null;
    } else if (upper === 'L') {
      x = scanner.number() + (relative ? x : 0);
      y = scanner.number() + (relative ? y : 0);
      commands.push({ type: 'line', x, y });
      lastControl = null;
    } else if (upper === 'H') {
      x = scanner.number() + (relative ? x : 0);
      commands.push({ type: 'line', x, y });
      lastControl = null;
    } else if (upper === 'V') {
      y = scanner.number() + (relative ? y : 0);
      commands.push({ type: 'line', x, y });
      lastControl = null;
    } else if (upper === 'C' || upper === 'S') {
      let x1: number;
      let y1: number;
      if (upper === 'C') {
        x1 = scanner.number() + (relative ? x : 0);
        y1 = scanner.number() + (relative ? y : 0);
      } else {
        // Smooth cubic: the first control mirrors the previous one.
        x1 = lastControl ? 2 * x - lastControl.x : x;
        y1 = lastControl ? 2 * y - lastControl.y : y;
      }
      const x2 = scanner.number() + (relative ? x : 0);
      const y2 = scanner.number() + (relative ? y : 0);
      const nx = scanner.number() + (relative ? x : 0);
      const ny = scanner.number() + (relative ? y : 0);
      commands.push({ type: 'cubic', x1, y1, x2, y2, x: nx, y: ny });
      lastControl = { x: x2, y: y2 };
      x = nx; y = ny;
    } else if (upper === 'Q' || upper === 'T') {
      let cx: number;
      let cy: number;
      if (upper === 'Q') {
        cx = scanner.number() + (relative ? x : 0);
        cy = scanner.number() + (relative ? y : 0);
      } else {
        cx = lastControl ? 2 * x - lastControl.x : x;
        cy = lastControl ? 2 * y - lastControl.y : y;
      }
      const nx = scanner.number() + (relative ? x : 0);
      const ny = scanner.number() + (relative ? y : 0);
      commands.push({ type: 'quadratic', x1: cx, y1: cy, x: nx, y: ny });
      lastControl = { x: cx, y: cy };
      x = nx; y = ny;
    } else if (upper === 'A') {
      const rx = scanner.number();
      const ry = scanner.number();
      const rotation = scanner.number();
      const largeArc = scanner.flag();
      const sweep = scanner.flag();
      const nx = scanner.number() + (relative ? x : 0);
      const ny = scanner.number() + (relative ? y : 0);
      commands.push(...arcToCubics(x, y, rx, ry, rotation, largeArc, sweep, nx, ny));
      lastControl = null;
      x = nx; y = ny;
    } else {
      throw new SvgParseError(`The path command ${describeToken(command)} is not valid SVG.`);
    }
    previous = command;
    if (commands.length > SVG_LIMITS.maxTotalCommands) throw new SvgParseError('This SVG has more path geometry than this reader allows.');
  }
  return commands;
}

/* ---------- basic shapes ---------- */

/**
 * A shape attribute in user units.
 *
 * Percentages are rejected rather than resolved: they mean a fraction of the
 * viewport, and a shape whose size depends on the viewport is not the same
 * object once you edit the document it landed in.
 */
export function parseLength(value: string | undefined, name: string, fallback = 0): number {
  if (value === undefined) return fallback;
  const text = value.trim().toLowerCase();
  if (text.endsWith('%')) throw new SvgParseError(`This SVG sizes ${describeToken(name)} as a percentage, which this reader does not resolve.`);
  // Consume the entire length: parseFloat would silently turn 2cm into 2px.
  const match = /^([+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)(?:px)?$/.exec(text);
  const number = match ? Number(match[1]) : NaN;
  if (!Number.isFinite(number)) throw new SvgParseError(`${describeToken(name)}=${describeToken(value)} is not a length this reader understands. Use a unitless or px measurement.`);
  if (Math.abs(number) > 1e6) throw new SvgParseError(`${describeToken(name)} is far outside any usable document.`);
  return number;
}

/** Points list for `<polygon>` / `<polyline>`, as path commands. */
export function parsePoints(value: string, close: boolean): PathCommand[] {
  const numbers = value.trim().split(/[\s,]+/).filter(Boolean).map((part) => {
    const number = Number(part);
    if (!Number.isFinite(number)) throw new SvgParseError(`The points list contains ${describeToken(part)}, which is not a number.`);
    return number;
  });
  if (numbers.length < 4 || numbers.length % 2 !== 0) return [];
  const commands: PathCommand[] = [{ type: 'move', x: numbers[0]!, y: numbers[1]! }];
  for (let at = 2; at + 1 < numbers.length; at += 2) commands.push({ type: 'line', x: numbers[at]!, y: numbers[at + 1]! });
  if (close) commands.push({ type: 'close' });
  return commands;
}

/* ---------- paint ---------- */

const NAMED_COLOURS: Readonly<Record<string, string>> = {
  black: '#000000', white: '#ffffff', red: '#ff0000', lime: '#00ff00', blue: '#0000ff',
  yellow: '#ffff00', cyan: '#00ffff', aqua: '#00ffff', magenta: '#ff00ff', fuchsia: '#ff00ff',
  silver: '#c0c0c0', gray: '#808080', grey: '#808080', maroon: '#800000', olive: '#808000',
  green: '#008000', purple: '#800080', teal: '#008080', navy: '#000080', orange: '#ffa500',
};

/** Canonical lowercase `#rrggbb`, or null when the value is not a solid colour. */
export function parseColour(value: string): string | null {
  return parseColourAlpha(value).colour;
}

/**
 * A solid colour and the alpha written into it. `rgba()`, `rgb(… / a)`,
 * `#rgba` and `#rrggbbaa` carry their own alpha; dropping it would import a
 * translucent shape as opaque without a word.
 */
export function parseColourAlpha(value: string): { colour: string | null; alpha: number } {
  const text = value.trim().toLowerCase();
  if (text === 'none' || text === 'transparent') return { colour: null, alpha: 1 };
  if (text === 'currentcolor' || text === 'inherit') {
    throw new SvgParseError(`This SVG uses ${describeToken(text)}, which has no meaning outside a document tree this reader does not build.`);
  }
  if (text.startsWith('url(')) {
    throw new SvgParseError('This SVG uses a gradient or pattern fill, which this reader does not support. Flatten it to solid colours.');
  }
  if (text.startsWith('#')) {
    const hex = text.slice(1);
    if (/^[0-9a-f]{3,4}$/.test(hex)) {
      return { colour: `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`, alpha: hex.length === 4 ? Number.parseInt(hex[3]! + hex[3]!, 16) / 255 : 1 };
    }
    if (/^[0-9a-f]{6}$/.test(hex)) return { colour: `#${hex}`, alpha: 1 };
    if (/^[0-9a-f]{8}$/.test(hex)) return { colour: `#${hex.slice(0, 6)}`, alpha: Number.parseInt(hex.slice(6), 16) / 255 };
    throw new SvgParseError(`${describeToken(value)} is not a colour this reader understands.`);
  }
  const rgb = /^rgba?\(([^)]*)\)$/.exec(text);
  if (rgb) {
    const parts = rgb[1]!.split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3 || parts.length > 4) throw new SvgParseError(`${describeToken(value)} is not a colour this reader understands.`);
    const number = (raw: string) => {
      const percent = raw.endsWith('%');
      const parsed = Number.parseFloat(percent ? raw.slice(0, -1) : raw);
      if (!Number.isFinite(parsed)) throw new SvgParseError(`${describeToken(value)} is not a colour this reader understands.`);
      return { parsed, percent };
    };
    const channel = (raw: string) => {
      const { parsed, percent } = number(raw);
      return Math.max(0, Math.min(255, Math.round(percent ? (parsed / 100) * 255 : parsed)));
    };
    const alpha = parts[3] === undefined ? 1 : (() => {
      const { parsed, percent } = number(parts[3]);
      return Math.max(0, Math.min(1, percent ? parsed / 100 : parsed));
    })();
    return { colour: `#${[0, 1, 2].map((index) => channel(parts[index]!).toString(16).padStart(2, '0')).join('')}`, alpha };
  }
  const named = NAMED_COLOURS[text];
  if (named) return { colour: named, alpha: 1 };
  throw new SvgParseError(`${describeToken(value)} is not a colour this reader understands.`);
}

/* ---------- transforms ---------- */

export function parseTransformList(value: string): Matrix {
  let matrix: Matrix = IDENTITY_MATRIX;
  const pattern = /([ \t\r\n,]*)([a-zA-Z]+)[ \t\r\n]*\(([^)]*)\)/gy;
  let match: RegExpExecArray | null;
  let end = 0;
  const invalid = () => new SvgParseError(`The transform ${describeToken(value)} is not a valid SVG transform list.`);
  while ((match = pattern.exec(value)) !== null) {
    // Consume the whole attribute, including the separators between functions.
    // A searching regexp alone silently drops malformed prefixes and suffixes.
    const gap = match[1]!;
    if (!(end === 0 ? /^[ \t\r\n]*$/ : /^[ \t\r\n,]+$/).test(gap)) throw invalid();
    end = pattern.lastIndex;
    const name = match[2]!;
    const body = match[3]!.replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, '');
    const parts = body.split(/[ \t\r\n]*,[ \t\r\n]*|[ \t\r\n]+/);
    if (parts.length > 6) throw invalid();
    const args = parts.map((part) => {
      if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(part)) throw invalid();
      const number = Number(part);
      if (!Number.isFinite(number)) throw new SvgParseError(`The transform ${describeToken(value)} contains a value that is not a number.`);
      return number;
    });
    const apply = (next: Matrix) => {
      matrix = multiplyMatrix(matrix, next);
      if (!matrix.every(Number.isFinite)) throw new SvgParseError('The SVG transform exceeds finite geometry limits.');
    };
    if (name === 'matrix' && args.length === 6) apply([args[0]!, args[1]!, args[2]!, args[3]!, args[4]!, args[5]!]);
    else if (name === 'translate' && (args.length === 1 || args.length === 2)) apply([1, 0, 0, 1, args[0]!, args[1] ?? 0]);
    else if (name === 'scale' && (args.length === 1 || args.length === 2)) apply([args[0]!, 0, 0, args[1] ?? args[0]!, 0, 0]);
    else if (name === 'rotate' && (args.length === 1 || args.length === 3)) {
      const radians = (args[0]! * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);
      if (args.length >= 3) {
        apply([1, 0, 0, 1, args[1]!, args[2]!]);
        apply([cos, sin, -sin, cos, 0, 0]);
        apply([1, 0, 0, 1, -args[1]!, -args[2]!]);
      } else apply([cos, sin, -sin, cos, 0, 0]);
    } else if (name === 'skewX' && args.length === 1) apply([1, 0, Math.tan((args[0]! * Math.PI) / 180), 1, 0, 0]);
    else if (name === 'skewY' && args.length === 1) apply([1, Math.tan((args[0]! * Math.PI) / 180), 0, 1, 0, 0]);
    else throw new SvgParseError(`The transform function ${describeToken(name)} is not one this reader supports.`);
  }
  if (!/^[ \t\r\n]*$/.test(value.slice(end))) throw invalid();
  const determinant = matrix[0] * matrix[3] - matrix[1] * matrix[2];
  if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-12) {
    throw new SvgParseError('This SVG contains a transform that collapses geometry to nothing, which cannot be edited.');
  }
  return matrix;
}
